// frontend/src/pages/WebhooksPage.jsx
/**
 * WebhooksPage — manage org webhook endpoints (P12-G13).
 *
 * Route: /settings/webhooks  (admin+ only; SettingsLayout wraps it)
 *
 * Features
 * --------
 * - List table: URL, Events, Status, Last Delivered, Failure Count, Delete
 * - Warning banner when one or more webhooks have been auto-disabled after
 *   10 consecutive delivery failures
 * - "Add Webhook" button → inline modal:
 *     · Endpoint URL  (HTTPS required, validated client-side)
 *     · Signing Secret (auto-generated 32-byte hex; copy-to-clipboard button)
 *     · Events        (checkboxes; currently only "scan.created")
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { createWebhook, deleteWebhook, listWebhooks } from "../api/webhooks";
import ConfirmDialog from "../components/ConfirmDialog";
import Spinner from "../components/Spinner";

// ─── Constants ────────────────────────────────────────────────────────────── //

/** A webhook is considered auto-disabled when failure_count ≥ this value. */
const MAX_FAILURES = 10;

/** All supported event types. Only scan.created exists today. */
const ALL_EVENTS = [
  { value: "scan.created", label: "scan.created — a QR code was scanned" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────── //

/** Generate a cryptographically random 32-byte hex string for the signing secret. */
function generateSecret() {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Copy text to clipboard and show a toast. */
async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard.");
  } catch {
    toast.error("Could not copy — please copy manually.");
  }
}

/** Minimal relative time: "just now", "2 min ago", "3 hr ago", "5 days ago". */
function relativeTime(isoString) {
  if (!isoString) return "Never";
  const delta = (Date.now() - new Date(isoString).getTime()) / 1000;
  if (delta < 60) return "just now";
  if (delta < 3600) return `${Math.floor(delta / 60)} min ago`;
  if (delta < 86400) return `${Math.floor(delta / 3600)} hr ago`;
  const d = Math.floor(delta / 86400);
  return `${d} ${d === 1 ? "day" : "days"} ago`;
}

// ─── Add Webhook Modal ────────────────────────────────────────────────────── //

/**
 * Modal for registering a new webhook.  Handles its own form state so the
 * parent doesn't re-render on every keystroke.
 */
function AddWebhookModal({ onClose, onCreated }) {
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState(() => generateSecret());
  const [events, setEvents] = useState({ "scan.created": true });
  const [urlError, setUrlError] = useState("");
  const [saving, setSaving] = useState(false);
  const urlRef = useRef(null);

  // Focus URL on open
  useEffect(() => {
    urlRef.current?.focus();
  }, []);

  const toggleEvent = (value) =>
    setEvents((prev) => ({ ...prev, [value]: !prev[value] }));

  const selectedEvents = Object.entries(events)
    .filter(([, on]) => on)
    .map(([v]) => v);

  const validate = () => {
    if (!url.startsWith("https://")) {
      setUrlError("URL must start with https://");
      return false;
    }
    setUrlError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (selectedEvents.length === 0) {
      toast.error("Select at least one event type.");
      return;
    }
    setSaving(true);
    try {
      const created = await createWebhook({ url, secret, events: selectedEvents });
      toast.success("Webhook registered.");
      onCreated(created);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ??
        (typeof err?.response?.data === "string" ? err.response.data : null);
      toast.error(detail ?? "Could not create webhook. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 space-y-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            Add Webhook
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            QRFlow will send a signed POST request to your endpoint when the
            selected events fire.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* URL */}
          <div>
            <label
              htmlFor="wh-url"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Endpoint URL
            </label>
            <input
              id="wh-url"
              ref={urlRef}
              type="url"
              placeholder="https://example.com/webhook"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                if (urlError) setUrlError("");
              }}
              onBlur={validate}
              className={`w-full rounded-lg border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                urlError ? "border-red-400" : "border-gray-300"
              }`}
            />
            {urlError && (
              <p className="mt-1 text-xs text-red-500">{urlError}</p>
            )}
          </div>

          {/* Signing Secret */}
          <div>
            <label
              htmlFor="wh-secret"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Signing Secret
              <span className="ml-1.5 text-xs font-normal text-gray-400">
                (auto-generated — save it now, it won't be shown again)
              </span>
            </label>
            <div className="flex gap-2">
              <input
                id="wh-secret"
                type="text"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                className="flex-1 min-w-0 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => copyToClipboard(secret)}
                className="shrink-0 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                title="Copy secret"
              >
                Copy
              </button>
              <button
                type="button"
                onClick={() => setSecret(generateSecret())}
                className="shrink-0 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                title="Regenerate secret"
              >
                ↺
              </button>
            </div>
          </div>

          {/* Events */}
          <div>
            <p className="block text-sm font-medium text-gray-700 mb-2">
              Events
            </p>
            <div className="space-y-2">
              {ALL_EVENTS.map(({ value, label }) => (
                <label
                  key={value}
                  className="flex items-center gap-2.5 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={!!events[value]}
                    onChange={() => toggleEvent(value)}
                    className="h-4 w-4 rounded border-gray-300 accent-blue-600"
                  />
                  <span className="text-sm text-gray-700 font-mono">
                    {label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? "Saving…" : "Add Webhook"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────── //

export default function WebhooksPage() {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null); // webhook object to delete
  const [deleting, setDeleting] = useState(false);

  // ── Load ────────────────────────────────────────────────────────────────── //
  const load = useCallback(async () => {
    try {
      const data = await listWebhooks();
      setWebhooks(data);
    } catch {
      toast.error("Could not load webhooks.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ── Handlers ────────────────────────────────────────────────────────────── //
  const handleCreated = (created) => {
    setWebhooks((prev) => [...prev, created]);
    setShowAddModal(false);
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteWebhook(pendingDelete.id);
      setWebhooks((prev) => prev.filter((w) => w.id !== pendingDelete.id));
      toast.success("Webhook deleted.");
    } catch {
      toast.error("Could not delete webhook. Please try again.");
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  };

  // ── Derived ─────────────────────────────────────────────────────────────── //
  const autoDisabled = webhooks.filter(
    (w) => !w.is_active && w.failure_count >= MAX_FAILURES,
  );

  // ── Render ──────────────────────────────────────────────────────────────── //
  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 mb-1">Webhooks</h1>
          <p className="text-sm text-gray-500">
            Receive a signed HTTP POST to your endpoint when events fire in
            QRFlow.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAddModal(true)}
          className="shrink-0 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
        >
          + Add Webhook
        </button>
      </div>

      {/* Auto-disabled warning banner */}
      {autoDisabled.length > 0 && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
          <svg
            viewBox="0 0 20 20"
            fill="currentColor"
            className="w-5 h-5 text-amber-500 shrink-0 mt-0.5"
          >
            <path
              fillRule="evenodd"
              d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
              clipRule="evenodd"
            />
          </svg>
          <div className="text-sm text-amber-800">
            <p className="font-semibold">
              {autoDisabled.length === 1
                ? "1 webhook has been auto-disabled"
                : `${autoDisabled.length} webhooks have been auto-disabled`}{" "}
              due to repeated delivery failures.
            </p>
            <p className="mt-0.5 text-amber-700">
              Fix the endpoint and re-enable the webhook — or delete and
              recreate it.
            </p>
          </div>
        </div>
      )}

      {/* Webhooks table */}
      {webhooks.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white py-12 text-center">
          <p className="text-sm text-gray-500">
            No webhooks yet.{" "}
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="text-blue-600 hover:underline"
            >
              Add your first webhook
            </button>
            .
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3 text-left">URL</th>
                <th className="px-4 py-3 text-left">Events</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Last Delivered</th>
                <th className="px-4 py-3 text-left">Failures</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {webhooks.map((wh) => (
                <tr key={wh.id} className="hover:bg-gray-50 transition-colors">
                  {/* URL */}
                  <td className="px-4 py-3 max-w-xs">
                    <span
                      className="block truncate font-mono text-xs text-gray-700"
                      title={wh.url}
                    >
                      {wh.url}
                    </span>
                  </td>

                  {/* Events */}
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(wh.events ?? []).map((ev) => (
                        <span
                          key={ev}
                          className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-mono text-[11px]"
                        >
                          {ev}
                        </span>
                      ))}
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3">
                    {wh.is_active ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                        Disabled
                      </span>
                    )}
                  </td>

                  {/* Last Delivered */}
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {relativeTime(wh.last_delivered_at)}
                  </td>

                  {/* Failure Count */}
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs font-medium ${
                        wh.failure_count >= MAX_FAILURES
                          ? "text-red-600"
                          : wh.failure_count > 0
                          ? "text-amber-600"
                          : "text-gray-500"
                      }`}
                    >
                      {wh.failure_count}
                    </span>
                  </td>

                  {/* Delete */}
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => setPendingDelete(wh)}
                      className="text-gray-400 hover:text-red-600 transition-colors"
                      title="Delete webhook"
                    >
                      <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                        <path
                          fillRule="evenodd"
                          d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193v-.443A2.75 2.75 0 0011.25 1h-2.5zm0 1.5h2.5c.69 0 1.25.56 1.25 1.25v.325a43.49 43.49 0 00-5 0v-.325C7.5 3.06 8.06 2.5 8.75 2.5zm-1.72 5.76a.75.75 0 011.06-.04l1.91 1.79 1.91-1.79a.75.75 0 011.06 1.06l-1.79 1.91 1.79 1.91a.75.75 0 11-1.06 1.06L10 12.37l-1.91 1.79a.75.75 0 11-1.06-1.06l1.79-1.91-1.79-1.91a.75.75 0 01.04-1.06z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showAddModal && (
        <AddWebhookModal
          onClose={() => setShowAddModal(false)}
          onCreated={handleCreated}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          title="Delete webhook?"
          message={`This will permanently remove the webhook for ${pendingDelete.url}. Deliveries in flight may still complete.`}
          confirmLabel="Delete"
          dangerous
          loading={deleting}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}
